// @ts-nocheck
import { useInfiniteQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

export type FeedFilter = 'all' | 'following';

export interface WallPostAuthor {
  id: string;
  name: string | null;
  first_name?: string | null;
  last_name?: string | null;
  username: string | null;
  avatar_url: string | null;
}

export interface WallPost {
  id: string;
  content: string | null;
  media_urls: string[] | null;
  hashtags: string[] | null;
  created_at: string;
  updated_at: string;
  profile_id: string;
  comments_count: number;
  likes_count: number;
  reactions_count: Record<string, number> | null;
  share_count: number | null;
  post_type: string;
  is_pinned: boolean | null;
  is_boosted: boolean | null;
  author: WallPostAuthor;
}

interface FeedPage {
  posts: WallPost[];
  nextCursor: number | null;
}

const PAGE_SIZE = 20;

// Helper to fetch profiles for posts (no FK exists, so we do a separate query)
async function fetchProfilesForPosts(profileIds: string[]): Promise<Map<string, WallPostAuthor>> {
  if (profileIds.length === 0) return new Map();
  
  const { data, error } = await supabase
    .from('profiles')
    .select('id, name, username, avatar_url')
    .in('id', profileIds);
  
  if (error) {
    console.error('Error fetching profiles:', error);
    return new Map();
  }
  
  return new Map((data || []).map(p => [p.id, { ...p, first_name: null, last_name: null } as WallPostAuthor]));
}

async function fetchFeedPage(
  filter: FeedFilter,
  userId: string | undefined,
  pageParam: number,
  reactionFilter: string | null
): Promise<FeedPage> {
  // If we have a reaction filter, we need a different query approach
  if (reactionFilter) {
    // First get post IDs that have this reaction type, ordered by count
    const { data: reactionData, error: reactionError } = await supabase
      .from('wall_post_reactions')
      .select('post_id')
      .eq('reaction_type', reactionFilter);
    
    if (reactionError) throw reactionError;
    
    // Count reactions per post
    const postReactionCounts: Record<string, number> = {};
    reactionData?.forEach((r: any) => {
      postReactionCounts[r.post_id] = (postReactionCounts[r.post_id] || 0) + 1;
    });
    
    // Sort by count and get unique post IDs
    const sortedPostIds = Object.entries(postReactionCounts)
      .sort((a, b) => b[1] - a[1])
      .map(([id]) => id);
    
    if (sortedPostIds.length === 0) {
      return { posts: [], nextCursor: null };
    }
    
    // Apply pagination to the sorted IDs
    const paginatedIds = sortedPostIds.slice(pageParam, pageParam + PAGE_SIZE);
    
    if (paginatedIds.length === 0) {
      return { posts: [], nextCursor: null };
    }
    
    // Fetch posts by these IDs
    let query = supabase
      .from('wall_posts')
      .select(`
        id,
        content,
        media_urls,
        hashtags,
        created_at,
        updated_at,
        profile_id,
        comments_count,
        likes_count,
        reactions_count,
        share_count,
        post_type,
        is_pinned,
        is_boosted
      `)
      .eq('is_hidden', false)
      .in('id', paginatedIds);
    
    // Apply following filter if needed
    if (filter === 'following' && userId) {
      const { data: following } = await supabase
        .from('profile_follows')
        .select('followed_id')
        .eq('follower_id', userId);
      
      const followedIds = following?.map(f => f.followed_id) || [];
      if (followedIds.length > 0) {
        query = query.in('profile_id', followedIds);
      } else {
        return { posts: [], nextCursor: null };
      }
    }
    
    const { data, error } = await query;
    
    if (error) throw error;
    
    // Sort the results by reaction count (maintain our sorted order)
    const postsMap = new Map((data || []).map((p: any) => [p.id, p]));
    const orderedData = paginatedIds
      .map(id => postsMap.get(id))
      .filter(Boolean);
    
    // Fetch profiles separately
    const profileIds = [...new Set(orderedData.map((p: any) => p.profile_id))];
    const profilesMap = await fetchProfilesForPosts(profileIds);
    
    const posts: WallPost[] = orderedData.map((post: any) => ({
      ...post,
      media_urls: post.media_urls as string[] | null,
      hashtags: post.hashtags as string[] | null,
      reactions_count: post.reactions_count as Record<string, number> | null,
      author: profilesMap.get(post.profile_id) || { id: post.profile_id, name: null, username: null, avatar_url: null },
    }));
    
    return {
      posts,
      nextCursor: pageParam + PAGE_SIZE < sortedPostIds.length ? pageParam + PAGE_SIZE : null,
    };
  }
  
  // Standard query without reaction filter
  let query = supabase
    .from('wall_posts')
    .select(`
      id,
      content,
      media_urls,
      hashtags,
      created_at,
      updated_at,
      profile_id,
      comments_count,
      likes_count,
      reactions_count,
      share_count,
      post_type,
      is_pinned,
      is_boosted
    `)
    .eq('is_hidden', false)
    .order('is_pinned', { ascending: false })
    .order('created_at', { ascending: false })
    .range(pageParam, pageParam + PAGE_SIZE - 1);

  // Apply following filter
  if (filter === 'following' && userId) {
    const { data: following } = await supabase
      .from('profile_follows')
      .select('followed_id')
      .eq('follower_id', userId);

    const followedIds = following?.map(f => f.followed_id) || [];
    
    if (followedIds.length > 0) {
      query = query.in('profile_id', followedIds);
    } else {
      // No following, return empty
      return { posts: [], nextCursor: null };
    }
  }

  const { data, error } = await query;

  if (error) {
    console.error('Error fetching feed:', error);
    throw error;
  }

  // Fetch profiles separately (no FK relationship exists)
  const profileIds = [...new Set((data || []).map((p: any) => p.profile_id))];
  const profilesMap = await fetchProfilesForPosts(profileIds);

  const posts: WallPost[] = (data || []).map((post: any) => ({
    ...post,
    media_urls: post.media_urls as string[] | null,
    hashtags: post.hashtags as string[] | null,
    reactions_count: post.reactions_count as Record<string, number> | null,
    author: profilesMap.get(post.profile_id) || { id: post.profile_id, name: null, username: null, avatar_url: null },
  }));

  return {
    posts,
    nextCursor: posts.length === PAGE_SIZE ? pageParam + PAGE_SIZE : null,
  };
}

export function useWallFeed(filter: FeedFilter = 'all', reactionFilter: string | null = null) {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const query = useInfiniteQuery({
    queryKey: ['wall-feed', filter, reactionFilter, user?.id],
    queryFn: ({ pageParam = 0 }) => fetchFeedPage(filter, user?.id, pageParam, reactionFilter),
    getNextPageParam: (lastPage) => lastPage.nextCursor,
    initialPageParam: 0,
    staleTime: 1000 * 60 * 2, // 2 minutes
  });

  const allPosts = query.data?.pages.flatMap(page => page.posts) || [];

  const invalidateFeed = () => {
    queryClient.invalidateQueries({ queryKey: ['wall-feed'] });
  };

  return {
    posts: allPosts,
    isLoading: query.isLoading,
    isFetchingNextPage: query.isFetchingNextPage,
    hasNextPage: query.hasNextPage,
    fetchNextPage: query.fetchNextPage,
    invalidateFeed,
    error: query.error,
  };
}
